export interface FileUploadSelection {
  file: File
  sourcePath?: string
  /**
   * Identifies which upload (a single drop, or one file/folder picker action)
   * this file came in on. Browsers only expose paths relative to the dropped /
   * picked root, so two folders named `src` from different parent directories
   * both arrive as `src/...` and are otherwise indistinguishable. Tagging each
   * upload root lets consumers keep them separate instead of merging them.
   */
  uploadId?: string
}

export type FileUploadInput = File | FileUploadSelection

const IGNORED_FILE_NAMES: ReadonlySet<string> = new Set(['.DS_Store'])

let uploadCounter = 0

/** A fresh id for one upload root (one drop entry, or one picker action). */
export function nextUploadId(): string {
  uploadCounter += 1
  return `upload-${Date.now().toString(36)}-${uploadCounter.toString(36)}`
}

function isFileEntry(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile
}

function isDirectoryEntry(
  entry: FileSystemEntry
): entry is FileSystemDirectoryEntry {
  return entry.isDirectory
}

function isIgnoredFile(file: File): boolean {
  return IGNORED_FILE_NAMES.has(file.name)
}

function normalizeSourcePath(
  sourcePath: string | undefined
): string | undefined {
  const trimmed = sourcePath?.replace(/^\/+/, '').trim()
  return trimmed || undefined
}

export function getFileSourcePath(file: File): string | undefined {
  return normalizeSourcePath(file.webkitRelativePath)
}

export function normalizeFileUploadInput(
  input: FileUploadInput,
  uploadId?: string
): FileUploadSelection {
  if (input instanceof File) {
    return {
      file: input,
      sourcePath: getFileSourcePath(input),
      uploadId,
    }
  }

  return {
    file: input.file,
    sourcePath:
      normalizeSourcePath(input.sourcePath) ?? getFileSourcePath(input.file),
    uploadId: input.uploadId ?? uploadId,
  }
}

export function createFileUploadSelections(
  files: Iterable<File>,
  uploadId: string = nextUploadId()
): FileUploadSelection[] {
  return Array.from(files)
    .filter((file) => !isIgnoredFile(file))
    .map((file) => normalizeFileUploadInput(file, uploadId))
}

function readFileEntry(
  entry: FileSystemFileEntry,
  uploadId: string
): Promise<FileUploadSelection[]> {
  return new Promise((resolve, reject) => {
    entry.file(
      (file) => {
        if (isIgnoredFile(file)) {
          resolve([])
          return
        }

        resolve([
          {
            file,
            sourcePath: normalizeSourcePath(entry.fullPath),
            uploadId,
          },
        ])
      },
      (error) => reject(error)
    )
  })
}

// `readEntries` returns directory contents in batches and signals completion
// with an empty batch, so we drain it recursively until that sentinel arrives.
function readAllDirectoryEntries(
  reader: FileSystemDirectoryReader,
  accumulated: FileSystemEntry[] = []
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    reader.readEntries((batch) => {
      if (batch.length === 0) {
        resolve(accumulated)
        return
      }
      accumulated.push(...batch)
      resolve(readAllDirectoryEntries(reader, accumulated))
    }, reject)
  })
}

async function readDirectoryEntry(
  entry: FileSystemDirectoryEntry,
  uploadId: string
): Promise<FileUploadSelection[]> {
  const entries = await readAllDirectoryEntries(entry.createReader())

  const nested = await Promise.allSettled(
    entries.map((child) => readEntry(child, uploadId))
  )
  return nested.flatMap((result) =>
    result.status === 'fulfilled' ? result.value : []
  )
}

function readEntry(
  entry: FileSystemEntry,
  uploadId: string
): Promise<FileUploadSelection[]> {
  if (isFileEntry(entry)) {
    return readFileEntry(entry, uploadId)
  }

  if (isDirectoryEntry(entry)) {
    return readDirectoryEntry(entry, uploadId)
  }

  return Promise.resolve([])
}

function getEntryFromDataTransferItem(
  item: DataTransferItem
): FileSystemEntry | null {
  return item.webkitGetAsEntry?.() ?? null
}

export async function flattenDataTransferFiles(
  dataTransfer: DataTransfer
): Promise<FileUploadSelection[]> {
  const items = Array.from(dataTransfer.items ?? [])
  const entries = items
    .map(getEntryFromDataTransferItem)
    .filter((entry): entry is FileSystemEntry => Boolean(entry))

  if (entries.length === 0) {
    return createFileUploadSelections(dataTransfer.files ?? [])
  }

  // Each top-level entry is its own upload root, so two dropped folders that
  // happen to share a name (e.g. two `src` dirs) stay separate instead of
  // merging under a single tree node.
  const selections = (
    await Promise.allSettled(
      entries.map((entry) => readEntry(entry, nextUploadId()))
    )
  ).flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
  if (selections.length > 0) return selections

  return createFileUploadSelections(dataTransfer.files ?? [])
}
